import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { FileText, Users, RefreshCw, Plus, Check, Play, X, ArrowLeft, AlertTriangle, Package } from 'lucide-react';

function formatCents(c) { return c != null ? `$${(c / 100).toFixed(2)}` : '—'; }
function formatDate(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }

const BATCH_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700', approved: 'bg-blue-100 text-blue-800',
  running: 'bg-amber-100 text-amber-800', completed: 'bg-emerald-100 text-emerald-800',
  canceled: 'bg-gray-100 text-gray-500', partial_failure: 'bg-red-100 text-red-700',
};
const ITEM_STATUS_COLORS = {
  candidate: 'bg-slate-100 text-slate-600', approved_for_run: 'bg-blue-100 text-blue-700',
  sent: 'bg-emerald-100 text-emerald-800', worked: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-700', skipped: 'bg-amber-100 text-amber-700', canceled: 'bg-gray-100 text-gray-500',
};
function StatusBadge({ status, map }) {
  const c = (map || BATCH_STATUS_COLORS)[status] || 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c}`}>{(status || '').replace(/_/g, ' ')}</span>;
}

// === CANDIDATES VIEW ===
function CandidatesPanel({ type, onCreateBatch }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [eligibleOnly, setEligibleOnly] = useState(true);

  const endpoint = type === 'statements' ? '/revenue/statement-batches/candidates' : '/revenue/collections-batches/candidates';

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint, { params: { eligible_only: eligibleOnly, limit: 50 } });
      setCandidates(res.data.candidates || []);
    } catch { setCandidates([]); }
    finally { setLoading(false); }
  }, [endpoint, eligibleOnly]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => { const eligible = candidates.filter(c => c.candidate_status === 'ELIGIBLE'); setSelected(new Set(eligible.map(c => c.artifact_id || c.action_id))); };

  if (loading) return <div className="p-4 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>;

  const idKey = type === 'statements' ? 'artifact_id' : 'action_id';

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/10">
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</span>
          <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={eligibleOnly} onCheckedChange={setEligibleOnly} className="h-3.5 w-3.5" />Eligible only</label>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={selectAll}>Select All Eligible</Button>
          <Button size="sm" variant="default" className="h-7 text-xs gap-1" onClick={() => onCreateBatch(Array.from(selected))} disabled={selected.size === 0} data-testid="create-batch-btn"><Plus className="h-3 w-3" />Create Batch ({selected.size})</Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={fetch}><RefreshCw className="h-3 w-3" /></Button>
        </div>
      </div>
      {candidates.length === 0 ? <div className="p-6 text-center text-xs text-muted-foreground">No candidates available.</div> : (
        <table className="w-full text-xs">
          <thead><tr className="border-b bg-muted/20">
            <th className="px-2 py-1.5 w-8"></th>
            <th className="px-2 py-1.5 text-left">{type === 'statements' ? 'Artifact' : 'Action'}</th>
            <th className="px-2 py-1.5 text-left">Patient</th>
            <th className="px-2 py-1.5 text-left">Balance</th>
            <th className="px-2 py-1.5 text-left">Status</th>
            <th className="px-2 py-1.5 text-left">Reason</th>
          </tr></thead>
          <tbody>{candidates.map((c, i) => {
            const id = c[idKey];
            const eligible = c.candidate_status === 'ELIGIBLE';
            return (
              <tr key={id || i} className={`border-b ${eligible ? 'hover:bg-muted/10' : 'opacity-50'}`}>
                <td className="px-2 py-1.5"><Checkbox checked={selected.has(id)} onCheckedChange={() => toggle(id)} disabled={!eligible} className="h-3.5 w-3.5" /></td>
                <td className="px-2 py-1.5 font-mono">{(id || '').slice(0, 10)}...</td>
                <td className="px-2 py-1.5">{(c.patient_id || '').slice(0, 14)}</td>
                <td className="px-2 py-1.5 font-mono">{formatCents(c.balance_amount_cents || c.balance_snapshot_cents)}</td>
                <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] ${eligible ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{c.candidate_status}</span></td>
                <td className="px-2 py-1.5 text-muted-foreground max-w-48 truncate">{c.candidate_reason}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}

// === BATCH LIST ===
function BatchListPanel({ type, onSelect, refreshKey }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const endpoint = type === 'statements' ? '/revenue/statement-batches' : '/revenue/collections-batches';

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(endpoint, { params: { limit: 20 } }); setBatches(res.data.batches || []); }
    catch { setBatches([]); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);

  if (loading) return <div className="p-4 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>;
  if (!batches.length) return <div className="p-6 text-center text-xs text-muted-foreground">No batches yet.</div>;

  return (
    <table className="w-full text-xs" data-testid="batch-list">
      <thead><tr className="border-b bg-muted/20">
        <th className="px-2 py-1.5 text-left">Batch</th><th className="px-2 py-1.5 text-left">Status</th>
        <th className="px-2 py-1.5 text-left">Items</th><th className="px-2 py-1.5 text-left">Results</th>
        <th className="px-2 py-1.5 text-left">Created</th>
      </tr></thead>
      <tbody>{batches.map(b => (
        <tr key={b.id} className="border-b hover:bg-muted/10 cursor-pointer" onClick={() => onSelect(b.id)} data-testid={`batch-row-${b.id?.slice(0, 8)}`}>
          <td className="px-2 py-1.5 font-mono">{(b.id || '').slice(0, 10)}...</td>
          <td className="px-2 py-1.5"><StatusBadge status={b.batch_status} /></td>
          <td className="px-2 py-1.5">{b.included_count || b.candidate_count || 0}</td>
          <td className="px-2 py-1.5">
            {(b.sent_count || b.worked_count) > 0 && <span className="text-emerald-600 mr-1">{b.sent_count || b.worked_count}ok</span>}
            {b.failed_count > 0 && <span className="text-red-600 mr-1">{b.failed_count}fail</span>}
            {b.skipped_count > 0 && <span className="text-amber-600">{b.skipped_count}skip</span>}
          </td>
          <td className="px-2 py-1.5 text-muted-foreground">{formatDate(b.created_at)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// === BATCH DETAIL ===
function BatchDetail({ batchId, type, onBack }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const endpoint = type === 'statements' ? `/revenue/statement-batches/${batchId}` : `/revenue/collections-batches/${batchId}`;

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(endpoint); setBatch(res.data.batch); }
    catch { setBatch(null); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { fetch(); }, [fetch]);

  const doAction = async (action) => {
    try {
      const ep = type === 'statements' ? `/revenue/statement-batches/${batchId}/${action}` : `/revenue/collections-batches/${batchId}/${action}`;
      await api.post(ep, action === 'cancel' ? { reason: 'Canceled from UI' } : {});
      toast.success(`Batch ${action} successful.`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || `${action} failed.`); }
  };

  if (loading) return <div className="p-4 space-y-2"><Skeleton className="h-8 w-48" /><Skeleton className="h-32 w-full" /></div>;
  if (!batch) return <div className="p-6 text-center"><p className="text-sm text-muted-foreground">Batch not found.</p><Button size="sm" onClick={onBack} className="mt-2">Back</Button></div>;

  const items = batch.items || [];
  const st = batch.batch_status;

  return (
    <div className="space-y-4" data-testid="batch-detail">
      <Button variant="ghost" size="sm" className="gap-1" onClick={onBack}><ArrowLeft className="h-3 w-3" />Back to Batches</Button>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Batch: {(batch.id || '').slice(0, 12)}...</h2>
          <div className="text-xs text-muted-foreground mt-0.5">Created {formatDate(batch.created_at)} {batch.created_by && `by ${batch.created_by.slice(0, 8)}...`}</div>
        </div>
        <StatusBadge status={st} />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="border p-3 text-center"><div className="text-lg font-bold">{batch.included_count || batch.candidate_count || 0}</div><div className="text-[10px] text-muted-foreground">Items</div></Card>
        <Card className="border p-3 text-center"><div className="text-lg font-bold text-emerald-600">{batch.sent_count || batch.worked_count || 0}</div><div className="text-[10px] text-muted-foreground">Sent/Worked</div></Card>
        <Card className="border p-3 text-center"><div className="text-lg font-bold text-red-600">{batch.failed_count || 0}</div><div className="text-[10px] text-muted-foreground">Failed</div></Card>
        <Card className="border p-3 text-center"><div className="text-lg font-bold text-amber-600">{batch.skipped_count || 0}</div><div className="text-[10px] text-muted-foreground">Skipped</div></Card>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {st === 'draft' && <Button size="sm" className="gap-1 text-xs" onClick={() => doAction('approve')} data-testid="batch-approve"><Check className="h-3 w-3" />Approve</Button>}
        {(st === 'approved' || st === 'draft') && <Button size="sm" className="gap-1 text-xs" variant="default" onClick={() => doAction('run')} data-testid="batch-run"><Play className="h-3 w-3" />Run</Button>}
        {(st === 'draft' || st === 'approved') && <Button size="sm" className="gap-1 text-xs" variant="outline" onClick={() => doAction('cancel')} data-testid="batch-cancel"><X className="h-3 w-3" />Cancel</Button>}
        <Button size="sm" variant="ghost" className="gap-1 text-xs" onClick={fetch}><RefreshCw className="h-3 w-3" />Refresh</Button>
      </div>

      {/* Items */}
      <Card className="border">
        <CardHeader className="py-2 px-3 border-b"><CardTitle className="text-sm">Items ({items.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? <div className="p-4 text-xs text-muted-foreground text-center">No items.</div> : (
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-muted/20">
                <th className="px-2 py-1.5 text-left">{type === 'statements' ? 'Artifact' : 'Action'}</th>
                <th className="px-2 py-1.5 text-left">Case</th>
                <th className="px-2 py-1.5 text-left">Patient</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Reason</th>
              </tr></thead>
              <tbody>{items.map((item, i) => (
                <tr key={item.id || i} className="border-b">
                  <td className="px-2 py-1.5 font-mono">{(item.artifact_id || item.action_id || '').slice(0, 10)}...</td>
                  <td className="px-2 py-1.5 font-mono">{(item.case_id || '').slice(0, 10)}...</td>
                  <td className="px-2 py-1.5">{(item.patient_id || '').slice(0, 12)}</td>
                  <td className="px-2 py-1.5"><StatusBadge status={item.run_status} map={ITEM_STATUS_COLORS} /></td>
                  <td className="px-2 py-1.5 text-muted-foreground max-w-48 truncate">{item.skip_reason || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// === CREATE BATCH DIALOG ===
function CreateBatchDialog({ open, onClose, type, selectedIds, onCreated }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const ep = type === 'statements' ? '/revenue/statement-batches' : '/revenue/collections-batches';
      const body = type === 'statements'
        ? { artifact_ids: selectedIds, reason, channel: 'email' }
        : { action_ids: selectedIds, reason };
      await api.post(ep, body);
      toast.success('Batch created.');
      setReason('');
      onClose();
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create batch.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create {type === 'statements' ? 'Statement' : 'Collections'} Batch</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-muted-foreground">{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</div>
          <Textarea placeholder="Reason (optional)..." value={reason} onChange={e => setReason(e.target.value)} className="text-sm min-h-[50px]" />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleCreate} disabled={submitting}>{submitting ? 'Creating...' : 'Create Batch'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// === MAIN PAGE ===
export default function RevenueBatchWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'statements';
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [createDialog, setCreateDialog] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreateBatch = (ids) => { setCreateDialog(ids); };
  const handleBatchCreated = () => { setRefreshKey(k => k + 1); };

  if (selectedBatchId) {
    return (
      <div className="flex-1 overflow-auto p-4 max-w-6xl">
        <BatchDetail batchId={selectedBatchId} type={tab} onBack={() => setSelectedBatchId(null)} />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-6xl" data-testid="batch-workspace">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2"><Package className="h-5 w-5" />Batch Management</h1>
        <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => navigate('/revenue')}><ArrowLeft className="h-3 w-3" />Revenue Workspace</Button>
      </div>

      <Tabs value={tab} onValueChange={v => { setSearchParams({ tab: v }); setSelectedBatchId(null); }}>
        <TabsList className="bg-muted/30 p-1">
          <TabsTrigger value="statements" className="text-xs gap-1.5" data-testid="batch-tab-statements"><FileText className="h-3.5 w-3.5" />Statement Batches</TabsTrigger>
          <TabsTrigger value="collections" className="text-xs gap-1.5" data-testid="batch-tab-collections"><Users className="h-3.5 w-3.5" />Collections Batches</TabsTrigger>
        </TabsList>

        {['statements', 'collections'].map(t => (
          <TabsContent key={t} value={t} className="space-y-4 mt-3">
            <Card className="border">
              <CardHeader className="py-2 px-3 border-b"><CardTitle className="text-sm">Candidates</CardTitle></CardHeader>
              <CardContent className="p-0">
                <CandidatesPanel type={t} onCreateBatch={handleCreateBatch} />
              </CardContent>
            </Card>
            <Card className="border">
              <CardHeader className="py-2 px-3 border-b"><CardTitle className="text-sm">Batches</CardTitle></CardHeader>
              <CardContent className="p-0">
                <BatchListPanel type={t} onSelect={setSelectedBatchId} refreshKey={refreshKey} />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {createDialog && (
        <CreateBatchDialog open={!!createDialog} onClose={() => setCreateDialog(null)} type={tab} selectedIds={createDialog} onCreated={handleBatchCreated} />
      )}
    </div>
  );
}
